import { map } from 'underscore';
import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dropdown,
  Label,
  Option,
} from '@fluentui/react-components';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { SwitchAdTypeDialog } from './switch-ad-type-dialog';
import { AdTypeStrings } from '../../constants/ad-type';

export const AdTypeSelector = ({
  i18n,
  adTypes,
  enableAdTypeSelector,
  selectedAdType,
  onAdTypeChanged,
  confirmWhenSwitchAdType,
}) => {
  const activity = useContext(ActivityContext);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [newAdType, setNewAdType] = useState(selectedAdType);

  const onOptionSelect = useCallback((_event, option) => {
    if (option.optionValue !== selectedAdType) {
      setNewAdType(option.optionValue);

      if (confirmWhenSwitchAdType) {
        setShowConfirmDialog(true);

        activity.signal(`onAdTypeChanging - ${JSON.stringify({
          oldAdType: selectedAdType,
          newAdType: option.optionValue,
        })}`);
      } else {
        onAdTypeChanged(option.optionValue);

        activity.signal(`onAdTypeChanged - ${JSON.stringify({
          oldAdType: selectedAdType,
          newAdType: option.optionValue,
        })}`);
      }
    }
  }, [activity, confirmWhenSwitchAdType, onAdTypeChanged, selectedAdType]);

  const onConfirm = useCallback(() => {
    setShowConfirmDialog(false);
    onAdTypeChanged(newAdType);

    activity.signal(`onAdTypeChanged - ${JSON.stringify({
      oldAdType: selectedAdType,
      newAdType,
    })}`);
  }, [activity, newAdType, onAdTypeChanged, selectedAdType]);

  const onCancel = useCallback(() => {
    setShowConfirmDialog(false);

    activity.signal(`onAdTypeChangeCanceled - ${JSON.stringify({
      oldAdType: selectedAdType,
      newAdType,
    })}`);
  }, [activity, newAdType, selectedAdType]);

  return (
    <div className="ad-type-selector">
      <div className="ad-type-label">
        <Label weight="semibold">
          {i18n.getString(_TL_('Ad type'))}
        </Label>
        <HelpPopup
          topic="pop_BA_NewAdCreate_AdType"
          aria-label={i18n.getString(_TL_('Click to learn more about Ad type'))}
        />
      </div>
      <Dropdown
        className="ad-type-dropdown"
        aria-label={i18n.getString(_TL_('Ad type selector'))}
        value={i18n.getString(AdTypeStrings[selectedAdType])}
        selectedOptions={[selectedAdType]}
        onOptionSelect={onOptionSelect}
        disabled={!enableAdTypeSelector}
      >
        {map(adTypes, adType => (
          <Option key={adType} value={adType}>
            {i18n.getString(AdTypeStrings[adType])}
          </Option>
        ))}
      </Dropdown>
      <SwitchAdTypeDialog
        i18n={i18n}
        showDialog={showConfirmDialog}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </div>
  );
};

AdTypeSelector.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  enableAdTypeSelector: PropTypes.bool,
  adTypes: PropTypes.arrayOf(PropTypes.string),
  selectedAdType: PropTypes.string,
  onAdTypeChanged: PropTypes.func.isRequired,
  confirmWhenSwitchAdType: PropTypes.bool,
};

AdTypeSelector.defaultProps = {
  enableAdTypeSelector: true,
  adTypes: [],
  selectedAdType: undefined,
  confirmWhenSwitchAdType: false,
};
